// PreventiApp · Sprint 5 · Paso 2 · Hooks TanStack Query para el Dashboard.
//
// Aislamiento estricto por queryKey:
//   ['dashboard', userId, colegioId, rol, modo, endpoint, ...params]
//
// - userId + colegioId + rol garantizan que dos sesiones distintas nunca
//   compartan caché (aunque compartan el mismo pod).
// - modo (oficial/induccion) evita mezclar los dos dashboards separados.
// - Al signOut, AuthContext hace `queryClient.clear()` (ver AuthContext.jsx).
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, casosApi } from './dashboardApi';
import { useAuth } from '../context/AuthContext';

function useScope(modo = 'oficial') {
  const { profile } = useAuth();
  return {
    userId: profile?.id || 'anon',
    colegioId: profile?.colegio_id || null,
    rol: profile?.rol || 'anon',
    modo,
    ready: !!profile?.id,
  };
}

const key = (scope, endpoint, params = {}) => [
  'dashboard',
  scope.userId,
  scope.colegioId || 'global',
  scope.rol,
  scope.modo,
  endpoint,
  params,
];

// ---------------- Dashboard Oficial ----------------
export function useKpis() {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'kpis'),
    queryFn: () => dashboardApi.kpis(scope.colegioId),
    enabled: scope.ready,
  });
}

export function useAccionHoy() {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'accion-hoy'),
    queryFn: () => dashboardApi.accionHoy(scope.colegioId),
    enabled: scope.ready,
  });
}

export function useDistribucionDimensiones(dias = 30) {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'distribucion-dimensiones', { dias }),
    queryFn: () => dashboardApi.distribucionDimensiones(scope.colegioId, dias),
    enabled: scope.ready,
  });
}

export function useCursosFoco(limit = 8) {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'cursos-foco', { limit }),
    queryFn: () => dashboardApi.cursosFoco(scope.colegioId, limit),
    enabled: scope.ready,
  });
}

export function useCasosAbiertos() {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'casos-abiertos'),
    queryFn: () => dashboardApi.casosAbiertos(scope.colegioId),
    enabled: scope.ready,
  });
}

export function useEventosCentinela() {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'eventos-centinela'),
    queryFn: () => dashboardApi.eventosCentinela(scope.colegioId),
    enabled: scope.ready,
  });
}

export function useCasosVencidos() {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'casos-vencidos'),
    queryFn: () => dashboardApi.casosVencidos(scope.colegioId),
    enabled: scope.ready,
  });
}

// ---------------- Casos ----------------
// Nota: /api/casos/{id} devuelve seguimientos + observaciones (que pueden
// tener texto libre). El detalle NO se pre-cachea agresivo: staleTime bajo.
export function useCasoDetalle(id) {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'caso-detalle', { id }),
    queryFn: () => casosApi.detail(id),
    enabled: scope.ready && !!id,
    staleTime: 10_000, // 10s — el detalle cambia con acciones del usuario
  });
}

export function useAsignables(enabled = true) {
  const scope = useScope('oficial');
  return useQuery({
    queryKey: key(scope, 'asignables'),
    queryFn: () => casosApi.asignables(scope.colegioId),
    enabled: scope.ready && enabled && !!scope.colegioId,
    staleTime: 60_000, // el listado de usuarios activos cambia poco
  });
}

// ---------------- Inducción ----------------
export function useInduccionKpis() {
  const scope = useScope('induccion');
  return useQuery({
    queryKey: key(scope, 'induccion-kpis'),
    queryFn: () => dashboardApi.induccionKpis(scope.colegioId),
    enabled: scope.ready,
  });
}

export function useInduccionEvolucion() {
  const scope = useScope('induccion');
  return useQuery({
    queryKey: key(scope, 'induccion-evolucion'),
    queryFn: () => dashboardApi.induccionEvolucion(scope.colegioId),
    enabled: scope.ready,
  });
}
